import { FunctionComponent, useEffect, useState } from 'react'
import { Button, InputCheckbox, InlineAlert } from '../../../../components/BaseComponents'
import CanonicalModal from '../../../../components/BaseComponents/CanonicalModal/CanonicalModal'
import SimpleSortTable, {
	TableColumn,
} from '../../../../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import { FetchStatus } from '../../../../types/LoadingStates'
import { Sector } from '../../../../types/Sector'
import { WORST_SECTOR_LIMIT_QUANTITY } from '../Graphs/ECO2Sector'
import useApi from '../../../../hooks/useApi'
import { useSectorOptions } from './useSelectionLookups'

// This component should only refresh/be doing stuff when 'open' as it lives inside a modal and
// might otherwise be rendered invisibly and cause interference

// Sector endpoint does not have pagination

type IModalSectorSearch = {
	open: boolean
	onClose: () => void
	onChange?: (newValue?: Sector[]) => void
}

export const ModalSectorSearch: FunctionComponent<IModalSectorSearch> = ({
	open,
	onClose,
	onChange,
}) => {
	const [selection, setSelection] = useState<Sector[]>([])
	const api = useApi();
	const { current, status: sectorsStatus, error: sectorsError, sectors: allSectors, retry } = useSectorOptions(api, open)

	useEffect(() => {
		setSelection([])
	}, [open, api])

	// DataGrid Columns
	const columns: TableColumn[] = [
		{
			id: 'selected',
			label: '',
			columnClass: 'w-3', // narrow fixed column width
			renderer: (columnKey, rowData) => {
				return (
					<InputCheckbox
						className='rounded border-charcoal-300 text-algae-600'
						value={rowData._id}
						checked={rowData.selected}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							event.stopPropagation()

							if (selection.filter((sector) => sector.code === rowData._id).length > 0) {
								// Remove from selection
								setSelection([...selection.filter((sector) => sector.code !== rowData._id)])
							} else {
								// Check there's still room to add another
								if (selection.length !== WORST_SECTOR_LIMIT_QUANTITY) {
									// Add to selected
									setSelection([...selection, rowData.sectorObject])
								}
							}
						}}
					/>
				)
			},
		},
		{
			id: 'isicCode',
			label: 'Code',
			renderer: (columnKey, rowData) => {
				return <p className='text-white'>{rowData._id}</p>
			},
		},
		{ id: 'name', label: 'Description' },
	]

	// DataGrid data constructor
	const constructData = (dataSet: Sector[]) => [
		...dataSet.map((sector) => {
			return {
				_id: sector.code,
				name: sector.name,
				selected:
					selection.filter((selectedSupplier) => selectedSupplier.code === sector.code).length > 0,
				sectorObject: sector,
			}
		}),
	]

	return (
		<CanonicalModal
			open={open}
			onClose={onClose}
			size='small'
			title='Select sectors to compare'
			actions={
				selection.length > 0 ? (
					<Button
						type='Primary'
						text='Save selection'
						disabled={!current || sectorsStatus !== FetchStatus.loaded}
						onClick={() => {
							onChange && onChange(selection)
						}}
					/>
				) : null
			}
		>
			{current && sectorsStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching sectors associated with this organisation. If this problem
						persists, please contact the Neoni team.
					</p>
					<p className='text-white'>{sectorsError}</p>
					<Button type='Primary' text='Retry' onClick={retry} />
				</InlineAlert>
			)}

			<p className='text-white mt-8 mb-4'>
				Please select up to {WORST_SECTOR_LIMIT_QUANTITY} sectors to compare their embedded CO₂
				emissions:
			</p>

			{current && selection.length > 0 && (
				<>
					{selection.map((sector) => {
						return (
							<Button
								key={sector.code}
								type='Outlined'
								size='sm'
								additionalClass='mr-2 mb-2'
								text={`${sector.code} - ${sector.name}`}
								hasTrailingIcon
								icon='fa-solid fa-xmark'
								onClick={() => {
									// Remove from selection
									setSelection([
										...selection.filter((selectedSector) => selectedSector.code !== sector.code),
									])
								}}
							/>
						)
					})}
				</>
			)}

			{current && sectorsStatus !== FetchStatus.error && <SimpleSortTable
				isLoading={sectorsStatus === FetchStatus.loading}
				additionalClass='mt-4'
				data={constructData(allSectors)}
				columns={columns}
			/>}
		</CanonicalModal>
	)
}
